/**
 * Shared tile-layer providers + layer-selector options used by BOTH the
 * public `TripOverviewMap` and the editor `RouteMapViewer`.
 *
 * Fase 2B (b99.10.10, 2026-05-19) — Antes, cada componente tinha a sua
 * própria cópia de `TILE_LAYERS` (byte-identical) e o seletor segmentado
 * `Mapa/Satélite/Terreno` aparecia duplicado em pelo menos três sítios
 * (`Controls.jsx`, `RouteMapViewer.jsx` inline, `RouteMapViewer.jsx`
 * fullscreen). Centralizado aqui para que features futuras sejam
 * adicionadas em UM único sítio.
 *
 * b99.10.11 (2026-05-19) — Adicionada 4.ª camada **Híbrida**
 * (satélite + estradas + labels). Cada entrada pode ser:
 *  - objeto `{url, attribution}` (camada única — OSM, sat, topo)
 *  - array de objetos (camadas empilhadas, renderizadas por ordem —
 *    base primeiro, overlays por cima). Esri permite usar a sua
 *    World_Imagery como base e adicionar World_Transportation (estradas
 *    + nomes) como overlay transparente, dando uma vista "Google Hybrid"
 *    sem dependências pagas.
 *
 * Providers (free, attribution required):
 *  - 2D       → OpenStreetMap standard
 *  - sat      → Esri World Imagery
 *  - topo     → OpenTopoMap (SRTM-derived hillshading)
 *  - hybrid   → Esri World Imagery + Esri World Transportation
 */

export const TILE_LAYERS = {
  "2d": {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
  sat: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS",
  },
  topo: {
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution:
      'Map data: &copy; OSM, SRTM | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
  },
  // Híbrida: base de satélite + overlay transparente com estradas, vias
  // férreas e nomes de sítios. Vista preferida de motards: terreno real
  // do satélite com as estradas e nomes sobrepostos. Os "Reference" do
  // Esri são tiles PNG semi-transparentes desenhados para empilhar por
  // cima de uma base imagery.
  hybrid: [
    {
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      attribution:
        "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS",
    },
    {
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}",
      attribution: "",
      opacity: 1,
    },
    {
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      attribution: "",
      opacity: 1,
    },
  ],
};

/**
 * Segmented button options shown in the floating "Mapa | Satélite |
 * Terreno | Híbrido" group. Keep labels short — they render in a tight
 * inline pill.
 *
 * b99.10.64 (2026-05-22) — `labelKey` is the i18n key (resolved in
 * `LayerSelector.jsx`); `label` stays as a PT fallback so any
 * consumer that doesn't run through i18n still sees a string.
 */
export const LAYER_OPTIONS = [
  { id: "2d",     labelKey: "map.layer.2d",     label: "Mapa" },
  { id: "sat",    labelKey: "map.layer.sat",    label: "Satélite" },
  { id: "topo",   labelKey: "map.layer.topo",   label: "Terreno" },
  { id: "hybrid", labelKey: "map.layer.hybrid", label: "Híbrido" },
];

/**
 * Safe getter — falls back to "2d" if an unknown view id is passed.
 * Always returns an array of layers, even for single-layer views, so the
 * renderer can map() over it uniformly. Order matters: index 0 is the
 * base, subsequent layers are stacked on top (overlays).
 */
export function getTileLayers(view) {
  const entry = TILE_LAYERS[view] || TILE_LAYERS["2d"];
  return Array.isArray(entry) ? entry : [entry];
}
